import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classes from '@/components/Collections/Collection/Collection.module.scss';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { Button } from '@/components';
import AutoComplete from '@/features/googleMap/components/GoogleLocation/AutoComplete';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { hasName, displayName } from '@/utils/utils';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Collection from '@/utils/api/odas/collection';
import { modalContext } from '@/context/modal.context';
import { useRouter } from 'next/router';
import { toastContext } from '@/context/toast.context';
import EditModeButton from './EditModeButton';
import { SHARED_STATUS } from '@/hooks/useCollectionPermissions';
import { isSuccessResponse } from '@/constants/httpStatus';
import t from '@/utils/Language/Collection-translations';

const AUTHOR_TYPE = { SELF: 'self', ORG: 'org', OTHER: 'other' };

const VISIBILITY = {
  PUBLIC: 'public',
  PRIVATE: 'private',
  ORGANIZATION: 'organization',
  PUBLIC_AFFILIATED: 'public_affiliated'
};

const getSharedStatus = (number) => {
  switch (number) {
    case SHARED_STATUS.PRIVATE:
      return VISIBILITY.PRIVATE;
    case SHARED_STATUS.ORGANIZATION:
      return VISIBILITY.ORGANIZATION;
    case SHARED_STATUS.PUBLIC:
      return VISIBILITY.PUBLIC;
    case SHARED_STATUS.PUBLIC_AFFILIATED:
      return VISIBILITY.PUBLIC_AFFILIATED;
    default:
      return '';
  }
};

const getFullName = (user) => {
  return hasName(user) ? displayName(user, { onlyShowLastInitial: true }) : '';
};

const getDefaultValues = (collection) => ({
  first_name: '',
  last_name: '',
  title: collection.title,
  location: collection.region,
  display_on_org_page: false,
  author: collection.author,
  author_type: AUTHOR_TYPE.SELF,
  visibility: VISIBILITY.PUBLIC
});

const EditCollectionCard = ({ account, collection }) => {
  const router = useRouter();
  const { addToast } = useContext(toastContext);
  const { enqueueModal, closeModal } = useContext(modalContext);
  const accountAffiliation = account?.affiliation;
  const defaultLocation = collection.region;
  const autoCompleteElementId = 'editCollection-search-autocomplete';

  // Helpers needed before state initialization
  const computeOrganization = () => {
    if (collection?.owner_organization) {
      return {
        name: collection.owner_organization.name,
        id: collection.owner_organization.id
      };
    } else if (accountAffiliation?.is_approved) {
      return {
        id: accountAffiliation.fetchable_id,
        name: accountAffiliation.organization_name
      };
    }
    return {};
  };

  const getAuthorType = (org) => {
    if (collection.author === getFullName(account?.data)) return AUTHOR_TYPE.SELF;
    if (org && collection.author === org.name) return AUTHOR_TYPE.ORG;
    return AUTHOR_TYPE.OTHER;
  };

  // State
  const initialOrganization = computeOrganization();

  const [collectionOrganization, setCollectionOrganization] = useState(initialOrganization);
  const [collectionLocation, setCollectionLocation] = useState({ locationName: collection?.region });
  const [editCollection, setEditCollection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(() => {
    const visibility = getSharedStatus(collection.shared_status);
    return {
      ...getDefaultValues(collection),
      visibility,
      author_type: getAuthorType(initialOrganization),
      display_on_org_page: visibility === VISIBILITY.PUBLIC_AFFILIATED
    };
  });

  useEffect(() => {
    setCollectionOrganization(computeOrganization());
  }, [accountAffiliation]);

  const showNameField = () => {
    return !!account?.data?.first_name && !!account?.data?.last_name;
  };

  const getAuthorName = (authorType) => {
    switch (authorType) {
      case AUTHOR_TYPE.SELF:
        return getFullName(account?.data);
      case AUTHOR_TYPE.ORG:
        return collectionOrganization.name;
      case AUTHOR_TYPE.OTHER:
        return formData.author;
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    setFormData((prevState) => {
      let changes = {
        [e.target.name]: e.target.value
      };
      switch (e.target.name) {
        case 'display_on_org_page':
          changes[e.target.name] = e.target.checked;
          changes.visibility = e.target.checked ? VISIBILITY.PUBLIC_AFFILIATED : VISIBILITY.PUBLIC;
          break;
        case 'author_type':
          changes.author = getAuthorName(e.target.value);
          break;
        case 'visibility':
          changes.display_on_org_page = e.target.value.indexOf('public') >= 0 ? prevState.display_on_org_page : false;
          break;
      }

      return { ...prevState, ...changes };
    });
  };

  const isValid = (val) => {
    return val?.trim()?.length > 0;
  };

  const isSaveDisabled = useMemo(() => {
    const hasLocation = Object.keys(collectionLocation).length > 0 || (defaultLocation && collectionLocation !== false);
    const hasTitle = isValid(formData.title);

    let isAuthorValid = false;
    if (formData.author_type === AUTHOR_TYPE.SELF) {
      isAuthorValid = getFullName(account?.data) !== '' || (isValid(formData.first_name) && isValid(formData.last_name));
    } else if (formData.author_type === AUTHOR_TYPE.ORG) {
      isAuthorValid = isValid(collectionOrganization.name);
    } else {
      isAuthorValid = isValid(formData.author);
    }

    const isVisibilityValid = formData.visibility !== VISIBILITY.ORGANIZATION || isValid(collectionOrganization.name);

    return !(hasTitle && hasLocation && isAuthorValid && isVisibilityValid);
  }, [formData, collectionLocation, collectionOrganization, account, defaultLocation]);

  // API actions
  const updateCollection = () => {
    setIsSubmitting(true);

    const updatedCollection = {
      ...collection,
      title: formData?.title,
      region: collectionLocation?.locationName ?? collection.region
    };

    if (collectionLocation?.geometry?.location) {
      updatedCollection.lat = collectionLocation.geometry.location.lat();
      updatedCollection.long = collectionLocation.geometry.location.lng();
    }

    if (formData.author) {
      updatedCollection.author = formData.author;
    }

    if (formData.visibility) {
      updatedCollection.shared_status = formData.visibility;
    }

    Collection.updateCollection(collection.id, { list: updatedCollection })
      .then((resp) => {
        if (isSuccessResponse(resp.status)) {
          addToast({
            message: t('Your collection has been updated.'),
            type: 'success'
          });
          setEditCollection(false);
        } else {
          addToast({
            message: t('There was a problem updating your collection, please fill out the necessary information and try again.'),
            type: 'error'
          });
        }
      })
      .catch(() => {
        addToast({ type: 'error' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const resetForm = () => {
    setFormData({
      ...getDefaultValues(collection),
      author_type: getAuthorType(collectionOrganization),
      visibility: getSharedStatus(collection.shared_status)
    });
    setCollectionLocation({ locationName: collection?.region });
    setEditCollection(false);
  };

  const deleteCollection = () => {
    enqueueModal({
      name: 'ConfirmationModal',
      title: t('Delete collection?'),
      text: t('Are you sure you want to delete this collection? This cannot be undone and will affect anyone who has saved this collection.'),
      confirmButtonText: t('Delete collection'),
      declineButtonText: t('Cancel'),
      buttonOrder: ['decline', 'confirm'],
      onConfirm: () => {
        Collection.deleteCollection(collection.id)
          .then((resp) => {
            if (isSuccessResponse(resp.status)) {
              addToast({
                message: t('This collection has been deleted.'),
                type: 'success'
              });

              closeModal();
              router.push('/collections?page=my');
            } else {
              addToast({
                message: t('We could not delete this collection. Please make sure you are an owner before deleting it.'),
                type: 'error'
              });
            }
          })
          .catch(() => {
            addToast({ type: 'error' });
            closeModal();
          });
      },
      onError: () => {
        addToast({ type: 'error' });
        closeModal();
      }
    });
  };

  // Display helpers
  const getAuthorDisplayValue = () => {
    switch (formData.author_type) {
      case AUTHOR_TYPE.SELF:
        return getFullName(account?.data) || collection?.author || collection?.author_other || t('Your Name');
      case AUTHOR_TYPE.ORG:
        return collectionOrganization.name;
      default:
        return formData.author;
    }
  };

  const getVisibilityDisplayValue = () => {
    switch (formData.visibility) {
      case VISIBILITY.PUBLIC:
      case VISIBILITY.PUBLIC_AFFILIATED:
        return t('Public');
      case VISIBILITY.ORGANIZATION:
        return collectionOrganization.name;
      default:
        return t('Private');
    }
  };

  const renderTooltip = (prop) => (
    <Tooltip id="button-tooltip" {...prop}>
      <p>
        <strong>{t('Public')}</strong>: {t('Anybody can see this')}.
      </p>
      <p>
        <strong>{t('Organization')}</strong>: {t('Anyone affiliated with the organization can see it')}.
      </p>
      <p>
        <strong>{t('Private')}</strong>: {t('Only you and people you invite')}.
      </p>
    </Tooltip>
  );

  // ============================================
  // DISPLAY VIEW (Read-only mode)
  // ============================================
  const renderDisplayView = () => (
    <>
      <Col lg="7" className={classes.infoCardCol}>
        <div className="panel bottom-pane grey">
          <Row className={classes.infoCardRow}>
            <Col className={classes.infoCardCol}>
              <label className={classes.boldLabel}>{t('Collection name')}</label>
            </Col>
            <Col className={classes.infoCardCol}>{formData.title}</Col>
          </Row>
          <Row className={classes.infoCardRow}>
            <Col className={classes.infoCardCol}>
              <label className={classes.boldLabel}>{t('Region')}</label>
            </Col>
            <Col className={classes.infoCardCol}>{collection.region}</Col>
          </Row>
          <Row className={classes.infoCardRow}>
            <Col className={classes.infoCardCol}>
              <label className={classes.boldLabel}>{t('Author')}</label>
            </Col>
            <Col className={`${classes.authorInfo} ${classes.infoCardCol}`}>
              <div data-cy="modal-collection-author-button-group" className={classes.buttonGroup}>
                {getAuthorDisplayValue()}
              </div>
            </Col>
          </Row>
          <Row className={classes.infoCardRow}>
            <Col className={classes.infoCardCol}>
              <label className={classes.boldLabel}>{t('Who should see this?')}</label>
            </Col>
            <Col className={`${classes.authorInfo} ${classes.infoCardCol}`}>
              <div data-cy="modal-collection-authorization-button-group" className={classes.buttonGroup}>
                {getVisibilityDisplayValue()}
              </div>
            </Col>
          </Row>
        </div>
      </Col>
      <Col lg="1" className={classes.infoCardCol}>
        <EditModeButton onButtonClick={() => setEditCollection(true)} additionalClasses={classes.editModeButton} />
      </Col>
    </>
  );

  // ============================================
  // EDIT VIEW (Form mode)
  // ============================================
  const renderEditForm = () => (
    <>
      <Col lg="7" className={classes.infoCardCol}>
        <div className="panel bottom-pane grey">
          {/* Title & Region Fields */}
          <div className={`form-group mb-3 ${classes.formVerticallyConnected}`}>
            <div className="mb-3">
              <label className={classes.boldLabel}>
                {t('Collection name')}
                <span className={classes.requiredLabel}>*</span>
              </label>
              <Form.Control className={classes.titleInput} name="title" value={formData.title} onChange={handleChange} />
            </div>
            <div>
              <label className={classes.boldLabel}>
                {t('Region')}
                <span className={classes.requiredLabel}>*</span>
              </label>
              <AutoComplete
                elementId={autoCompleteElementId}
                locationObject={collectionLocation}
                saveLocationFn={setCollectionLocation}
                defaultLocation={defaultLocation}
                additionalClasses={classes.locationInput}
              />
            </div>
          </div>

          {/* Author & Visibility Fields */}
          <div className="form-group form-group-padded">
            {/* Author Selection */}
            <label className={classes.boldLabel}>
              {t('Author')}
              <span className={classes.requiredLabel}>*</span>
            </label>
            <div className={`${classes.authorInfo} mb-3`}>
              <div className={classes.selectWrapper}>
                <Form.Select
                  data-cy="modal-collection-author-select"
                  className={classes.selectDropdown}
                  value={formData.author_type}
                  onChange={(e) => handleChange({ target: { name: 'author_type', value: e.target.value } })}
                >
                  <option value={AUTHOR_TYPE.SELF}>
                    {getFullName(account?.data) || collection?.author || collection?.author_other || t('Your Name')}
                  </option>
                  {collectionOrganization.name && <option value={AUTHOR_TYPE.ORG}>{collectionOrganization.name}</option>}
                  <option value={AUTHOR_TYPE.OTHER}>{t('Other') + '...'}</option>
                </Form.Select>
                <FontAwesomeIcon icon={['fas', 'chevron-down']} className={classes.selectIcon} />
              </div>

              {/* Name inputs for 'self' when user has no name */}
              {!showNameField() && formData.author_type === AUTHOR_TYPE.SELF && (
                <div>
                  <label className={classes.boldLabel}>{t('Please tell us your first and last name')}</label>
                  <FloatingLabel controlId="formFirstNameLabel" label={t('First name')}>
                    <Form.Control name="first_name" placeholder={t('First Name')} value={formData.first_name} onChange={handleChange} />
                    <br />
                  </FloatingLabel>
                  <FloatingLabel controlId="formLastNameLabel" label={t('Last name')}>
                    <Form.Control name="last_name" placeholder={t('Last Name')} value={formData.last_name} onChange={handleChange} />
                  </FloatingLabel>
                </div>
              )}

              {/* Custom author input for 'other' */}
              {formData.author_type === AUTHOR_TYPE.OTHER && (
                <Form.Control
                  data-cy="modal-collection-author-other-input"
                  name="author"
                  placeholder={t('Author')}
                  value={formData.author}
                  onChange={handleChange}
                />
              )}
            </div>

            {/* Visibility Selection */}
            <label className={classes.boldLabel}>
              <div>
                {t('Who should see this?')}&nbsp;
                <OverlayTrigger placement="top" overlay={renderTooltip}>
                  <span data-cy="modal-collection-authorization-tooltip">
                    <FontAwesomeIcon icon={['fas', 'question-circle']} />
                  </span>
                </OverlayTrigger>
              </div>
            </label>
            <div className={classes.authorInfo}>
              <div className={classes.selectWrapper}>
                <Form.Select
                  data-cy="modal-collection-authorization-select"
                  className={classes.selectDropdown}
                  value={formData.visibility}
                  onChange={(e) => handleChange({ target: { name: 'visibility', value: e.target.value } })}
                >
                  <option value={VISIBILITY.PUBLIC}>{t('Public')}</option>
                  {collectionOrganization.name && <option value={VISIBILITY.ORGANIZATION}>{collectionOrganization.name}</option>}
                  <option value={VISIBILITY.PRIVATE}>{t('Private')}</option>
                </Form.Select>
                <FontAwesomeIcon icon={['fas', 'chevron-down']} className={classes.selectIcon} />
              </div>

              {formData.visibility?.indexOf('public') >= 0 && collectionOrganization.id && (
                <Form.Check
                  type="checkbox"
                  id="default-checkbox"
                  onChange={handleChange}
                  name="display_on_org_page"
                  label={t("Display this collection on my organization's page")}
                  className="my-2"
                  checked={formData.display_on_org_page}
                />
              )}

              {formData.visibility === VISIBILITY.ORGANIZATION && (
                <p>{t("This collection will be displayed on my organization's page and only people affiliated with my organization can see it.")}</p>
              )}

              <Button
                dataCy="modal-collection-button-delete"
                type="link"
                onButtonClick={deleteCollection}
                text={t('Delete Collection')}
                additionalClasses={classes.deleteCollectionBtn}
              />
            </div>
          </div>
        </div>
      </Col>
    </>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <Card className={classes.collectionInformation}>
      <Card.Body className={classes.collectionDetails}>
        <Row className={classes.infoCardRow}>
          <Col lg="3" className={classes.infoCardCol}>
            <div className={classes.collectionEditLabel}>
              <FontAwesomeIcon icon={['fas', 'edit']} />
              &nbsp;&nbsp; {t('Edit collection')}
            </div>
          </Col>
          <Col lg="1" className={`${classes.divider} ${classes.infoCardCol}`}></Col>

          {editCollection ? renderEditForm() : renderDisplayView()}
        </Row>

        {editCollection && (
          <div className={classes.editCollectionBtn}>
            <Button
              dataCy="modal-collection-button-cancel"
              type="link"
              onButtonClick={resetForm}
              text={t('Cancel')}
              additionalClasses={`${classes.actionButton} ${classes.cancelBtn}`}
              disabled={isSubmitting}
            />
            <Button
              dataCy="modal-collection-button-update"
              type="primary"
              icon={{ style: 'fas', font: 'check' }}
              disabled={isSaveDisabled || isSubmitting}
              onButtonClick={updateCollection}
              text={t('Save')}
              additionalClasses={classes.actionButton}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

EditCollectionCard.propTypes = {
  account: PropTypes.shape({
    data: PropTypes.object,
    affiliation: PropTypes.object
  }),
  collection: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    region: PropTypes.string,
    author: PropTypes.string,
    shared_status: PropTypes.number,
    owner_organization: PropTypes.object
  }).isRequired
};

export default EditCollectionCard;
